import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import DownloadButton from "../components/DownloadButton";
import { useMarkets, useRates } from "../hooks/useData";
import { BRAND, siteUrl } from "../utils/config";
import { titleCase } from "../utils/data";

const FAQ = [
  {
    q: "What is satta matka?",
    a: "Satta matka is a number-based betting game that began in Mumbai in the 1960s. Players choose numbers and the result is declared as an open pana, a jodi and a close pana. Today the results and historical charts of the different markets are followed online.",
  },
  {
    q: "What is the difference between Jodi and Pana?",
    a: "A pana (also called patti or panel) is a three-digit number. A jodi is a two-digit number made from the last digit of the open pana sum and the last digit of the close pana sum.",
  },
  {
    q: "What is a matka chart?",
    a: "A matka chart is the history of a market's results. A Jodi chart lists the two-digit jodi of every day; a Pana (panel) chart also shows the open and close pana. Charts are grouped week by week.",
  },
  {
    q: "What does the Kalyan matka chart show?",
    a: "The Kalyan chart records the open pana, jodi and close pana declared by the Kalyan market for each day, so you can see its earlier results at a glance.",
  },
  {
    q: "Is satta matka legal?",
    a: "Gambling laws differ from state to state and country to country, and betting games like satta matka are restricted or prohibited in many places. Check the law where you live. This website only publishes result information.",
  },
  {
    q: "How is the jodi calculated?",
    a: "Add the three digits of the open pana and keep the last digit. Do the same with the close pana. The two digits together form the jodi. For example, open pana 579 adds up to 21 (digit 1) and close pana 113 adds up to 5, so the jodi is 15.",
  },
];

const SattaMatka = () => {
  const { markets } = useMarkets();
  const rates = useRates();
  const origin = siteUrl();

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "Satta Matka Guide", item: `${origin}/satta-matka` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Satta Matka Guide — Results, Jodi & Pana Charts Explained",
      inLanguage: "en-IN",
      mainEntityOfPage: `${origin}/satta-matka`,
      image: `${origin}/og-image.png`,
      publisher: { "@type": "Organization", name: BRAND.name, logo: { "@type": "ImageObject", url: `${origin}/icon-512.png` } },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <main className="rt-main">
      <Seo
        title={`Satta Matka Guide – Matka Result, Jodi & Pana Chart Explained | ${BRAND.name}`}
        description="Learn what satta matka is, how matka results work, how to read Jodi and Pana (panel) charts, how the jodi is calculated and which markets are covered — Kalyan, Milan, Madhur, Main Bazar and more."
        path="/satta-matka"
        schema={schema}
        type="article"
      />

      <section className="rt-phead">
        <div className="rt-wrap">
          <nav className="rt-crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <b>Satta Matka Guide</b>
          </nav>
          <span className="rt-eyebrow rt-eyebrow--dark">Guide</span>
          <h1>Satta Matka — Results, Jodi &amp; Pana Charts Explained</h1>
          <p>Everything you need to read a matka result and a matka chart, in simple words.</p>
        </div>
      </section>

      <section className="rt-wrap rt-section">
        <article className="rt-article rt-article--wide">
          <section>
            <h2>What is satta matka?</h2>
            <p>
              <strong>Satta matka</strong> is a number game that started in Mumbai in the 1960s and later grew into many
              named markets such as Kalyan, Milan, Madhur, Sridevi, Main Bazar, Rajdhani and Time Bazar. Each market
              declares its result twice a day: an <em>open</em> number and a <em>close</em> number. Millions of people
              follow these results every day, which is why a <Link to="/matka-result-today">live matka result</Link> page
              is the first thing most players check.
            </p>
          </section>

          <section>
            <h2>How a matka result is made</h2>
            <p>A full result has three parts:</p>
            <ul>
              <li>
                <strong>Open pana</strong> — a three-digit number declared at the market&apos;s opening time.
              </li>
              <li>
                <strong>Jodi</strong> — a two-digit number built from the last digit of the open pana sum and the last
                digit of the close pana sum.
              </li>
              <li>
                <strong>Close pana</strong> — a three-digit number declared at closing time.
              </li>
            </ul>
            <p>
              Example: open pana <b>579</b> → 5+7+9 = 21 → digit 1. Close pana <b>113</b> → 1+1+3 = 5 → digit 5. The
              result is <b>579 - 15 - 113</b>.
            </p>
          </section>

          <section>
            <h2>Jodi chart vs Pana (panel) chart</h2>
            <p>
              A <strong>Jodi chart</strong> lists the two-digit jodi of a market for every day. A{" "}
              <strong>Pana chart</strong> (also called a <em>panel chart</em> or <em>patti chart</em>) shows the open
              pana, the jodi and the close pana. On {BRAND.name} both are shown week by week, Monday to Sunday, with the
              newest week first and a search box to find any number.
            </p>
          </section>

          <section>
            <h2>Types of pana (patti)</h2>
            <ul>
              <li>
                <strong>Single pana</strong> — all three digits are different (for example 579).
              </li>
              <li>
                <strong>Double pana</strong> — two digits are the same (for example 223).
              </li>
              <li>
                <strong>Triple pana</strong> — all three digits are the same (for example 777).
              </li>
            </ul>
          </section>

          <section>
            <h2>Game rates</h2>
            <p>Current payout rates shown on {BRAND.name} (stake – return):</p>
            <div className="rt-article__table">
              <table>
                <thead>
                  <tr>
                    <th>Game</th>
                    <th>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map(([label, value]) => (
                    <tr key={label}>
                      <td>{label}</td>
                      <td>{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2>Matka markets covered</h2>
            <p>Open any market to see its full Jodi or Pana chart:</p>
            <div className="rt-pills rt-pills--left">
              {markets.map((m) => (
                <Link key={m.slug} to={`/jodi/${m.slug}`}>
                  {titleCase(m.name)}
                </Link>
              ))}
            </div>
          </section>

          <section>
            <h2>Frequently asked questions</h2>
            {FAQ.map((f) => (
              <div key={f.q} className="rt-article__qa">
                <h3>{f.q}</h3>
                <p>{f.a}</p>
              </div>
            ))}
          </section>

          <section>
            <h2>Play responsibly</h2>
            <p>
              Satta matka involves financial risk and is restricted or prohibited in many places. This website publishes
              result information only. You must be 18 or older, should follow the law where you live and should never
              wager money you cannot afford to lose. Read our <Link to="/disclaimer">disclaimer</Link> and{" "}
              <Link to="/terms">terms</Link>.
            </p>
          </section>
        </article>
        <div className="rt-cta-row">
          <DownloadButton />
        </div>
      </section>
    </main>
  );
};

export default SattaMatka;
